import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import {
  FileText,
  Download,
  Eye,
  MousePointer,
  TrendingUp,
  DollarSign,
  Heart,
  MessageCircle,
  Share2,
  UserPlus,
  Calendar,
  Loader2,
} from "lucide-react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Calendar as CalendarComponent } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { format } from "date-fns";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

export default function Reports({ clients, metrics, adMetrics, API }) {
  const [selectedClient, setSelectedClient] = useState("");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportData, setReportData] = useState(null);
  const reportRef = useRef(null);

  const generateReport = () => {
    if (!selectedClient) {
      toast.error("Please select a client");
      return;
    }

    const client = clients.find((c) => c.id === selectedClient);
    let filteredMetrics = metrics.filter((m) => m.client_id === selectedClient);
    let filteredAdMetrics = adMetrics.filter((m) => m.client_id === selectedClient);

    if (dateRange.from && dateRange.to) {
      const fromStr = format(dateRange.from, "yyyy-MM-dd");
      const toStr = format(dateRange.to, "yyyy-MM-dd");
      filteredMetrics = filteredMetrics.filter(
        (m) => m.date >= fromStr && m.date <= toStr
      );
      filteredAdMetrics = filteredAdMetrics.filter(
        (m) => m.date >= fromStr && m.date <= toStr
      );
    }

    // Calculate stats
    const stats = {
      impressions: filteredMetrics.reduce((sum, m) => sum + m.impressions, 0),
      clicks: filteredMetrics.reduce((sum, m) => sum + m.clicks, 0),
      spend: filteredMetrics.reduce((sum, m) => sum + m.spend, 0),
      likes: filteredMetrics.reduce((sum, m) => sum + m.likes, 0),
      comments: filteredMetrics.reduce((sum, m) => sum + m.comments, 0),
      shares: filteredMetrics.reduce((sum, m) => sum + m.shares, 0),
      new_followers: filteredMetrics.reduce((sum, m) => sum + m.new_followers, 0),
    };
    stats.ctr = stats.impressions > 0 ? (stats.clicks / stats.impressions) * 100 : 0;
    stats.engagement = stats.likes + stats.comments + stats.shares;

    // Platform breakdown
    const platformStats = {};
    filteredMetrics.forEach((m) => {
      if (!platformStats[m.platform]) {
        platformStats[m.platform] = {
          impressions: 0,
          clicks: 0,
          engagement: 0,
        };
      }
      platformStats[m.platform].impressions += m.impressions;
      platformStats[m.platform].clicks += m.clicks;
      platformStats[m.platform].engagement += m.likes + m.comments + m.shares;
    });

    // Top ads
    const topAds = [...filteredAdMetrics]
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, 5);

    // Chart data
    const chartData = filteredMetrics
      .slice(0, 30)
      .reverse()
      .map((m) => ({
        date: m.date,
        impressions: m.impressions,
        clicks: m.clicks,
      }));

    setReportData({
      client,
      dateRange:
        dateRange.from && dateRange.to
          ? `${format(dateRange.from, "MMM d, yyyy")} - ${format(dateRange.to, "MMM d, yyyy")}`
          : "All Time",
      stats,
      platformStats,
      topAds,
      chartData,
      generatedAt: new Date().toLocaleString(),
    });

    toast.success("Report generated!");
  };

  const downloadPDF = async () => {
    if (!reportRef.current) return;

    setIsGenerating(true);
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      pdf.save(`${reportData.client.name}_report.pdf`);

      toast.success("PDF downloaded!");
    } catch (e) {
      console.error("Error generating PDF:", e);
      toast.error("Failed to generate PDF");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="reports">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 font-['Outfit']">
          Reports
        </h1>
        <p className="text-slate-500 mt-1">
          Generate and export performance reports
        </p>
      </div>

      {/* Report Controls */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl p-6 shadow-sm border border-slate-100"
      >
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <Label>Client</Label>
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger className="mt-1" data-testid="report-client-select">
                <SelectValue placeholder="Select client" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Date Range</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="mt-1 w-full justify-start"
                  data-testid="report-date-range"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  {dateRange.from && dateRange.to
                    ? `${format(dateRange.from, "MMM d")} - ${format(dateRange.to, "MMM d")}`
                    : "All Time"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="range"
                  selected={dateRange}
                  onSelect={setDateRange}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
          </div>

          <Button
            onClick={generateReport}
            className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-6 shadow-lg shadow-violet-500/20"
            data-testid="generate-report-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Generate Report
          </Button>
        </div>
      </motion.div>

      {/* Report Preview */}
      {reportData && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex justify-end mb-4">
            <Button
              onClick={downloadPDF}
              disabled={isGenerating}
              className="bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-700 hover:to-pink-700 text-white rounded-full px-6"
              data-testid="download-pdf-btn"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </>
              )}
            </Button>
          </div>

          {/* Report Content */}
          <div
            ref={reportRef}
            className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden"
            data-testid="report-preview"
          >
            {/* Report Header */}
            <div className="bg-gradient-to-r from-violet-600 to-pink-600 p-8 text-white">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center text-2xl font-bold">
                  {reportData.client.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2 className="text-2xl font-bold font-['Outfit']">
                    {reportData.client.name}
                  </h2>
                  <p className="text-white/80">Performance Report</p>
                </div>
              </div>
              <div className="flex gap-6 text-sm">
                <div>
                  <span className="text-white/60">Period:</span>{" "}
                  <span className="font-medium">{reportData.dateRange}</span>
                </div>
                <div>
                  <span className="text-white/60">Generated:</span>{" "}
                  <span className="font-medium">{reportData.generatedAt}</span>
                </div>
              </div>
            </div>

            {/* Metrics Grid */}
            <div className="p-8">
              <h3 className="text-lg font-semibold text-slate-900 font-['Outfit'] mb-4">
                Key Metrics
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <Eye className="w-4 h-4" />
                    Impressions
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    {reportData.stats.impressions.toLocaleString()}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <MousePointer className="w-4 h-4" />
                    Clicks
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    {reportData.stats.clicks.toLocaleString()}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <TrendingUp className="w-4 h-4" />
                    CTR
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    {reportData.stats.ctr.toFixed(2)}%
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <DollarSign className="w-4 h-4" />
                    Spend
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    ${reportData.stats.spend.toLocaleString()}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <Heart className="w-4 h-4" />
                    Likes
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    {reportData.stats.likes.toLocaleString()}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <MessageCircle className="w-4 h-4" />
                    Comments
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    {reportData.stats.comments.toLocaleString()}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <Share2 className="w-4 h-4" />
                    Shares
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    {reportData.stats.shares.toLocaleString()}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <UserPlus className="w-4 h-4" />
                    Followers
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    +{reportData.stats.new_followers.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Chart */}
              {reportData.chartData.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-slate-900 font-['Outfit'] mb-4">
                    Performance Trend
                  </h3>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={reportData.chartData}>
                        <defs>
                          <linearGradient id="colorImpRpt" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#7c3aed" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} />
                        <YAxis stroke="#94a3b8" fontSize={12} />
                        <Tooltip />
                        <Area
                          type="monotone"
                          dataKey="impressions"
                          stroke="#7c3aed"
                          fillOpacity={1}
                          fill="url(#colorImpRpt)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Platform Breakdown */}
              {Object.keys(reportData.platformStats).length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-slate-900 font-['Outfit'] mb-4">
                    Platform Breakdown
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(reportData.platformStats).map(([platform, stats]) => (
                      <div
                        key={platform}
                        className="border border-slate-200 rounded-lg p-4"
                      >
                        <h4 className="font-medium text-slate-900 capitalize mb-3">
                          {platform}
                        </h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-500">Impressions</span>
                            <span className="font-medium">
                              {stats.impressions.toLocaleString()}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Clicks</span>
                            <span className="font-medium">
                              {stats.clicks.toLocaleString()}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Engagement</span>
                            <span className="font-medium">
                              {stats.engagement.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Ads */}
              {reportData.topAds.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 font-['Outfit'] mb-4">
                    Top Performing Ads
                  </h3>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="table-header text-left py-3">Ad Name</th>
                        <th className="table-header text-left py-3">Platform</th>
                        <th className="table-header text-right py-3">Impressions</th>
                        <th className="table-header text-right py-3">Clicks</th>
                        <th className="table-header text-right py-3">CTR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.topAds.map((ad, i) => (
                        <tr key={ad.id} className="border-b border-slate-100">
                          <td className="py-3 text-slate-900">{ad.ad_name}</td>
                          <td className="py-3 text-slate-600 capitalize">
                            {ad.platform}
                          </td>
                          <td className="py-3 text-right text-slate-900">
                            {ad.impressions.toLocaleString()}
                          </td>
                          <td className="py-3 text-right text-slate-900">
                            {ad.clicks.toLocaleString()}
                          </td>
                          <td className="py-3 text-right text-slate-900">
                            {ad.ctr.toFixed(2)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Report Footer */}
            <div className="bg-slate-50 px-8 py-4 border-t border-slate-100">
              <p className="text-sm text-slate-500 text-center">
                Generated by MetricFlow • Social Media Agency Dashboard
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Empty State */}
      {!reportData && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white rounded-xl p-12 shadow-sm border border-slate-100 text-center"
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-slate-100 flex items-center justify-center">
            <FileText className="w-10 h-10 text-slate-400" />
          </div>
          <h3 className="text-xl font-semibold text-slate-900 font-['Outfit'] mb-2">
            No Report Generated
          </h3>
          <p className="text-slate-500 max-w-md mx-auto">
            Select a client and date range, then click "Generate Report" to create
            a comprehensive performance report.
          </p>
        </motion.div>
      )}
    </div>
  );
}
